import {
  CommandId,
  MessageId,
  ModelSelection,
  ProviderInteractionMode,
  RuntimeMode,
  ThreadId,
  type ModelSelection as ModelSelectionValue,
  type ProviderInteractionMode as ProviderInteractionModeValue,
  type RuntimeMode as RuntimeModeValue,
} from "@cmmd-forge/contracts";
import * as Schema from "effect/Schema";

import type { TerminalContextDraft } from "./lib/terminalContext";

export const PersistedComposerImageAttachmentSchema = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  mimeType: Schema.String,
  sizeBytes: Schema.Number,
  dataUrl: Schema.String,
});
export type PersistedComposerImageAttachmentData =
  typeof PersistedComposerImageAttachmentSchema.Type;

export const PersistedComposerFileAttachmentSchema = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  mimeType: Schema.String,
  sizeBytes: Schema.Number,
});
export type PersistedComposerFileAttachmentData = typeof PersistedComposerFileAttachmentSchema.Type;

export const PersistedTerminalContextDraftSchema = Schema.Struct({
  id: Schema.String,
  threadId: ThreadId,
  createdAt: Schema.String,
  terminalId: Schema.String,
  terminalLabel: Schema.String,
  lineStart: Schema.Number,
  lineEnd: Schema.Number,
});
export type PersistedTerminalContextDraftData = typeof PersistedTerminalContextDraftSchema.Type;

export const PersistedStagedComposerSubmissionSchema = Schema.Struct({
  prompt: Schema.String,
  attachments: Schema.Array(PersistedComposerImageAttachmentSchema),
  files: Schema.optionalKey(Schema.Array(PersistedComposerFileAttachmentSchema)),
  terminalContexts: Schema.Array(PersistedTerminalContextDraftSchema),
  startedAt: Schema.String,
  commandId: Schema.optionalKey(CommandId),
  messageId: Schema.optionalKey(MessageId),
  modelSelection: Schema.optionalKey(ModelSelection),
  runtimeMode: Schema.optionalKey(RuntimeMode),
  interactionMode: Schema.optionalKey(ProviderInteractionMode),
  executionIntent: Schema.optionalKey(
    Schema.Struct({ runtimeId: Schema.String, intentId: Schema.optionalKey(Schema.String) }),
  ),
});
export type PersistedStagedComposerSubmissionData =
  typeof PersistedStagedComposerSubmissionSchema.Type;

export interface StagedComposerSubmission {
  readonly prompt: string;
  readonly attachments: readonly PersistedComposerImageAttachmentData[];
  readonly files: readonly PersistedComposerFileAttachmentData[];
  readonly terminalContexts: readonly TerminalContextDraft[];
  readonly startedAt: string;
  readonly commandId?: CommandId;
  readonly messageId?: MessageId;
  readonly modelSelection?: ModelSelectionValue;
  readonly runtimeMode?: RuntimeModeValue;
  readonly interactionMode?: ProviderInteractionModeValue;
  readonly executionIntent?: { readonly runtimeId: string; readonly intentId?: string };
}

interface StagedSubmissionNormalizers {
  readonly attachment: (value: unknown) => PersistedComposerImageAttachmentData | null;
  readonly file: (value: unknown) => PersistedComposerFileAttachmentData | null;
  readonly terminalContext: (value: unknown) => PersistedTerminalContextDraftData | null;
  readonly modelSelection: (value: unknown) => ModelSelectionValue | null;
  readonly runtimeMode: (value: unknown) => value is RuntimeModeValue;
}

function normalizeEntries<T>(value: unknown, normalize: (value: unknown) => T | null): T[] {
  return Array.isArray(value) ? value.flatMap((entry) => normalize(entry) ?? []) : [];
}

function normalizeExecutionIntent(value: PersistedStagedComposerSubmissionData["executionIntent"]) {
  if (!value || typeof value.runtimeId !== "string") return undefined;
  return {
    runtimeId: value.runtimeId,
    ...(typeof value.intentId === "string" ? { intentId: value.intentId } : {}),
  };
}

function normalizeDispatchIdentity(candidate: Partial<PersistedStagedComposerSubmissionData>) {
  return {
    ...(typeof candidate.commandId === "string"
      ? { commandId: CommandId.make(candidate.commandId) }
      : {}),
    ...(typeof candidate.messageId === "string"
      ? { messageId: MessageId.make(candidate.messageId) }
      : {}),
  };
}

function normalizeDispatchSettings(
  candidate: Partial<PersistedStagedComposerSubmissionData>,
  normalizers: StagedSubmissionNormalizers,
) {
  const modelSelection = normalizers.modelSelection(candidate.modelSelection);
  return {
    ...(modelSelection ? { modelSelection } : {}),
    ...(normalizers.runtimeMode(candidate.runtimeMode)
      ? { runtimeMode: candidate.runtimeMode }
      : {}),
    ...(candidate.interactionMode === "default" || candidate.interactionMode === "plan"
      ? { interactionMode: candidate.interactionMode }
      : {}),
  };
}

export function normalizePersistedStagedComposerSubmission(
  value: unknown,
  normalizers: StagedSubmissionNormalizers,
): PersistedStagedComposerSubmissionData | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<PersistedStagedComposerSubmissionData>;
  if (typeof candidate.prompt !== "string" || typeof candidate.startedAt !== "string") return null;
  const executionIntent = normalizeExecutionIntent(candidate.executionIntent);
  return {
    prompt: candidate.prompt,
    attachments: normalizeEntries(candidate.attachments, normalizers.attachment),
    files: normalizeEntries(candidate.files, normalizers.file),
    terminalContexts: normalizeEntries(candidate.terminalContexts, normalizers.terminalContext),
    startedAt: candidate.startedAt,
    ...normalizeDispatchIdentity(candidate),
    ...normalizeDispatchSettings(candidate, normalizers),
    ...(executionIntent ? { executionIntent } : {}),
  };
}

function persistTerminalContext(context: TerminalContextDraft): PersistedTerminalContextDraftData {
  return {
    id: context.id,
    threadId: context.threadId,
    createdAt: context.createdAt,
    terminalId: context.terminalId,
    terminalLabel: context.terminalLabel,
    lineStart: context.lineStart,
    lineEnd: context.lineEnd,
  };
}

export function persistStagedComposerSubmission(
  submission: StagedComposerSubmission,
): PersistedStagedComposerSubmissionData {
  return {
    prompt: submission.prompt,
    attachments: [...submission.attachments],
    files: [...submission.files],
    terminalContexts: submission.terminalContexts.map(persistTerminalContext),
    startedAt: submission.startedAt,
    ...(submission.commandId ? { commandId: submission.commandId } : {}),
    ...(submission.messageId ? { messageId: submission.messageId } : {}),
    ...(submission.modelSelection ? { modelSelection: submission.modelSelection } : {}),
    ...(submission.runtimeMode ? { runtimeMode: submission.runtimeMode } : {}),
    ...(submission.interactionMode ? { interactionMode: submission.interactionMode } : {}),
    ...(submission.executionIntent ? { executionIntent: submission.executionIntent } : {}),
  };
}

export function hydrateStagedComposerSubmission(
  submission: PersistedStagedComposerSubmissionData | undefined,
): StagedComposerSubmission | null {
  if (!submission) return null;
  return {
    ...submission,
    attachments: [...submission.attachments],
    files: [...(submission.files ?? [])],
    terminalContexts: submission.terminalContexts.map((context) => ({ ...context, text: "" })),
  };
}
